import { Injectable } from "@angular/core";
import { ConfigService } from "core/config";
import { EProcessStatus, IPDFDocumentDto } from "core/dto";
import { dateUtils } from "core/utils";
import { BehaviorSubject, Subject, combineLatest, debounceTime, distinctUntilChanged, fromEvent, map, skip, startWith, take, takeUntil, tap } from "rxjs";
import { RemotePdfDocumentService } from "../../../remotes/remote-pdf-document.service";
import { RemoteProcessService } from "../../../remotes/remote-process.service";
import { SignProcessWizardService } from "../../../sign-process-wizard.service";
import { DocumentUploadStepService } from "../document-upload-step.service";



@Injectable()
export class DocumentCollectionService {

  private readonly _destroy = new Subject<void>()

  readonly pdfDocumentsSubject = this._documentUploadStepService.pdfDocumentsSubject;
  readonly processIdSubject = this._signProcessWizardService.processIdSubject;

  readonly draggingFilesSubject = new BehaviorSubject<boolean>(false);



  readonly dragDropHeightObs = this.draggingFilesSubject
    .pipe(
      distinctUntilChanged(),
      map(dragging => {
        if (!dragging) {
          return '7rem'
        }

        return '40rem'
      }))

  MAT_CARD_WIDTH = 450;

  readonly cardsPerRowObs = fromEvent(window, 'resize')
    .pipe(
      debounceTime(100),
      map(() => Math.floor(window.innerWidth / this.MAT_CARD_WIDTH) || 1),
      distinctUntilChanged(),
      startWith(window.innerWidth / this.MAT_CARD_WIDTH)
    )

  readonly pdfDocumentsGroupedObs = combineLatest([
    this.pdfDocumentsSubject,
    this.cardsPerRowObs
  ])
    .pipe(
      map(([pdfDocuments, cardsPerRow]) =>
        pdfDocuments.reduce<IPDFDocumentDto[][]>((total, current, idx) => {

          const arrIdx = Math.floor(idx / cardsPerRow);

          if (!total[arrIdx]) {
            total[arrIdx] = []
          }

          total[arrIdx].push(current);
          return total;

        },
          []
        )
      )
    )

  readonly dragOverObs = fromEvent(window, 'dragover')

  readonly dragEndObs = combineLatest([
    fromEvent(window, 'dragend').pipe(startWith(null)),
    fromEvent(window, 'blur').pipe(startWith(null)),
    fromEvent(window, 'drop').pipe(startWith(null)),
    fromEvent(window, 'mouseup').pipe(startWith(null)),
    fromEvent(window, 'mouseout').pipe(startWith(null))
  ])
    .pipe(
      tap((events => {
        events
          .filter(Boolean)
          .forEach((event) => {
            event.preventDefault();
          })
      }))
    )

  readonly dropObs = fromEvent(document, 'drop')

  constructor(

    private _documentUploadStepService: DocumentUploadStepService,
    private _signProcessWizardService: SignProcessWizardService,
    private _remotePdfDocumentService: RemotePdfDocumentService,
    private _remoteProcessService: RemoteProcessService,
    private readonly _configService: ConfigService

  ) {

    this.pdfDocumentsSubject
      .pipe(takeUntil(this._destroy))
      .subscribe(() => {
        this.draggingFilesSubject.next(false)
      })

    //drag end
    this.dragEndObs
      .pipe(
        takeUntil(this._destroy),
        skip(1)
      )
      .subscribe(() => this.draggingFilesSubject.next(false))


    //drag start
    this.dragOverObs
      .pipe(
        takeUntil(this._destroy)
      )
      .subscribe(() => this.draggingFilesSubject.next(true))

    this.dropObs
      .pipe(takeUntil(this._destroy))
      .subscribe((evParams) => {
        evParams.preventDefault();
        evParams.stopPropagation();
        this.draggingFilesSubject.next(false)
      })
  }

  reorder(params: { previousIndex: number, currentIndex: number }) {
    const { currentIndex, previousIndex } = params;

    if (previousIndex === currentIndex) {
      return;
    }

    const pdfDocuments = [...(this.pdfDocumentsSubject.value || [])];
    const [movedPdfDocument] = pdfDocuments.splice(previousIndex, 1);
    pdfDocuments.splice(currentIndex, 0, movedPdfDocument)

    const max = Math.max(previousIndex, currentIndex);
    const min = Math.min(previousIndex, currentIndex);

    const reorderPairs = pdfDocuments
      // .filter((_, idx) => idx >= min && idx <= max)
      .map(({ id }, idx) => ({ key: id!, orderIdx: idx }))





    this._remotePdfDocumentService.reorderShallow(reorderPairs).pipe(take(1)).subscribe();
    this.pdfDocumentsSubject.next(pdfDocuments);

  }

  insertBatch(pdfDocuments: IPDFDocumentDto[]) {
    const processId = this.processIdSubject.value;

    const insertDocuments = () => {

      this._remotePdfDocumentService.insertBatch(pdfDocuments).pipe(take(1))
        .subscribe(docs => {

          this._remotePdfDocumentService.localCollectionStore.stateSubject.next([
            ...(this.pdfDocumentsSubject.value || []),
            ...pdfDocuments.map((doc, idx) => ({
              ...docs[idx],
              content: doc.content
            }))
          ])
        })

    }

    if (!processId) {

      const firstDocumentName = pdfDocuments[0].name;
      const processInfo = this._signProcessWizardService.processInfoSubject.value;

      const formData = processInfo;

      const description = formData?.description || firstDocumentName;
      const validTill = formData?.validTill || dateUtils.add('day', new Date(), this._configService.appConfig.processExpirationInDaysDefault);

      this._remoteProcessService.insert({
        status: EProcessStatus.Draft,
        ...processInfo,
        description,
        validTill,
      })
        .subscribe((processId: string) => {

          pdfDocuments.forEach((pdfDoc) => pdfDoc.processId = processId)
          insertDocuments();
        });

      return;
    }

    insertDocuments();

  }

  remove(uuid: string) {

    this._remotePdfDocumentService.remove(uuid).pipe(take(1)).subscribe();
  }

  ngOnDestroy() {

    this._destroy.next();
    this._destroy.complete();
    this.draggingFilesSubject.complete();

  }

}


